"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import {
  Button,
  Input,
  SegmentedControl,
  Typography,
  Toast,
  useToast,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  Modal,
} from "@artha/ui";
import {
  type PublicJobAlertSubscriptionController,
} from "../hooks/usePublicJobAlertSubscription";
import { useAudienceAnalytics } from "@/lib/analytics";
import { type JobAlertExperience } from "@artha/types";

interface JobAlertSubscriptionCardProps {
  cardId: string;
  controller: PublicJobAlertSubscriptionController;
  creatorName?: string;
}

export function JobAlertSubscriptionCard({
  cardId,
  controller,
  creatorName = "this creator",
}: JobAlertSubscriptionCardProps) {
  const t = useTranslations("profile");
  const { trackJobAlertEmailSubmitted, trackJobAlertPrefsSubmitted } = useAudienceAnalytics();
  const displayName = creatorName || "this creator";
  const feedback =
    controller.feedback.cardId === cardId ? controller.feedback : null;

  const { toast, showToast, hideToast } = useToast();
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (feedback?.successMessage) {
      showToast(feedback.successMessage);
    }
  }, [feedback?.successMessage, showToast]);

  const isValidEmail = (emailStr: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr.trim());
  };

  const handleCardSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const emailValue = controller.email.trim();
    if (!emailValue || !isValidEmail(emailValue)) {
      setLocalError(t("jobAlertInvalidEmail"));
      return;
    }
    setLocalError(null);
    trackJobAlertEmailSubmitted(emailValue);
    controller.setActiveModalCardId(cardId);
  };

  return (
    <section
      data-testid="job-alert-subscription-card"
      data-card-id={cardId}
      className="rounded-[8px] border border-black bg-[#FFF7D6] p-4 shadow-[0px_4px_0px_0px_#000000]"
    >
      <div className="space-y-3">
        <div className="space-y-1">
          <Typography variant="h4" className="text-[18px] font-semibold text-[#101828] line-clamp-1">
            {t("jobAlertCardTitle", { creatorName: displayName })}
          </Typography>
          <Typography variant="small" className="text-[14px] leading-5 text-[#475467]">
            {t("jobAlertCardDescription")}
          </Typography>
        </div>

        <form className="space-y-3" onSubmit={handleCardSubmit}>
          <label className="block space-y-1">
            <span className="text-[13px] font-medium text-[#344054]">
              {t("jobAlertEmailLabel")}
            </span>
            <Input
              type="email"
              value={controller.email}
              placeholder={t("jobAlertEmailPlaceholder")}
              onChange={(event) => {
                controller.setEmail(event.target.value);
                if (localError) setLocalError(null);
              }}
              autoComplete="email"
            />
          </label>

          {localError ? (
            <p className="text-[13px] font-medium text-[#B42318]">
              {localError}
            </p>
          ) : null}

          {feedback?.errorMessage ? (
            <p className="text-[13px] font-medium text-[#B42318]">
              {feedback.errorMessage}
            </p>
          ) : null}

          <Button
            type="submit"
            disabled={controller.isSubmitting}
            className="w-full rounded-[12px]"
          >
            {controller.isSubmitting
              ? t("jobAlertSavingLabel")
              : t("jobAlertSaveLabel")}
          </Button>
        </form>
      </div>

      <Modal
        isOpen={controller.activeModalCardId === cardId}
        onClose={() => controller.setActiveModalCardId(null)}
        showCloseButton
        className="mx-0 border border-black bg-[#FFF7D6] p-6 shadow-[0px_4px_0px_0px_#000000] !animate-none rounded-[8px]"
      >
        <div className="space-y-4 text-left">
          <Typography variant="h3" className="text-[18px] font-semibold text-[#101828]">
            {t("jobAlertModalTitle", { creatorName: displayName })}
          </Typography>
          <form
            className="space-y-4"
            onSubmit={async (event) => {
              event.preventDefault();
              const res = await controller.submit(cardId);
              if (res) {
                trackJobAlertPrefsSubmitted(
                  controller.email,
                  controller.frequency,
                  controller.experience
                );
                controller.setActiveModalCardId(null);
              }
            }}
          >
            <div className="space-y-1">
              <span className="block text-[13px] font-medium text-[#344054]">
                {t("jobAlertExperienceLabel")}
              </span>
              <Select
                value={controller.experience}
                onValueChange={(value) =>
                  controller.setExperience(value as JobAlertExperience)
                }
              >
                <SelectTrigger className="w-full" data-testid="experience-select-trigger">
                  <SelectValue placeholder={t("jobAlertExperienceLabel")} />
                </SelectTrigger>
                <SelectContent className="w-[var(--radix-select-trigger-width)]">
                  <SelectItem value="internship">{t("jobAlertInternship")}</SelectItem>
                  <SelectItem value="fresher">{t("jobAlertFresher")}</SelectItem>
                  <SelectItem value="1-3_years">{t("jobAlert1To3Years")}</SelectItem>
                  <SelectItem value="3-5_years">{t("jobAlert3To5Years")}</SelectItem>
                  <SelectItem value="5-7_years">{t("jobAlert5To7Years")}</SelectItem>
                  <SelectItem value="7-10_years">{t("jobAlert7To10Years")}</SelectItem>
                  <SelectItem value="10+_years">{t("jobAlert10PlusYears")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <span className="block text-[13px] font-medium text-[#344054]">
                {t("jobAlertFrequencyLabel")}
              </span>
              <SegmentedControl
                ariaLabel={t("jobAlertFrequencyLabel")}
                value={controller.frequency}
                onValueChange={(value) =>
                  controller.setFrequency(value as "daily" | "weekly")
                }
                options={[
                  { label: t("jobAlertDaily"), value: "daily" },
                  { label: t("jobAlertWeekly"), value: "weekly" },
                ]}
              />
            </div>

            {feedback?.errorMessage ? (
              <p className="text-[13px] font-medium text-[#B42318]">
                {feedback.errorMessage}
              </p>
            ) : null}

            <Button
              type="submit"
              disabled={controller.isSubmitting}
              className="w-full rounded-[12px]"
            >
              {controller.isSubmitting
                ? t("jobAlertSavingLabel")
                : t("jobAlertSaveLabel")}
            </Button>
          </form>
        </div>
      </Modal>

      <Toast
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </section>
  );
}
